<template>
  <div>
    <!-- Mockup bar: role switcher + back -->
    <div class="mockup-bar">
      <span class="mockup-badge">MOCKUP</span>
      <span class="mockup-bar-sep">|</span>
      <span class="mockup-bar-label">ACT Dashboard - Roles + Assignments + Workbench</span>
      <router-link to="/wireframes/dashboard" class="mockup-back">← Back</router-link>
    </div>

    <div class="layout">
      <!-- Sidebar -->
      <aside class="sidebar">
        <div class="section">
          <div class="section-title">
            Viewing as
            <span class="info-icon" tabindex="0" role="button"
              aria-label="Info: mock roles explain global vs per-participant authorization"
              data-tip="Mock role = portal-level capability (e.g., can write visits vs can write scoring). Per-participant write access comes from the Assignments table.">
              i
            </span>
          </div>

          <div class="role-row">
            <button
              v-for="r in roles"
              :key="r.id"
              class="role-btn"
              :class="{ active: currentRoleId === r.id }"
              @click="currentRoleId = r.id"
            >
              {{ r.label }}
            </button>
          </div>
        </div>

        <div class="section">
          <div class="section-title">
            lGroup
            <span class="info-icon" tabindex="0" role="button"
              aria-label="Info: lGroup is the ACT group used by DynamoDB"
              data-tip="An lGroup groups ACTInterview data in DynamoDB. Participants + visits + types are scoped by lGroup.">
              i
            </span>
          </div>
          <select v-model="selectedLGroup" class="select">
            <option v-for="lg in lGroups" :key="lg.id" :value="lg.id">{{ lg.label }}</option>
          </select>
        </div>

        <div class="section">
          <div class="section-title">
            Participant list
            <span class="info-icon" tabindex="0" role="button"
              aria-label="Info: participants come from ACTInterview DynamoDB"
              data-tip="Mock list = ACTInterview DynamoDB participants (ACTInterviewParticipant) for the chosen lGroup. Stored with ParticipantDisplayId = StudyParticipant.ServerCode.">
              i
            </span>
          </div>

          <input
            v-model="participantSearch"
            type="text"
            class="search"
            placeholder="Search participant display id..."
          />

          <div class="participant-list">
            <div
              v-for="p in filteredParticipants"
              :key="p.participantId"
              class="participant-row"
              :class="{ active: selectedParticipant && selectedParticipant.participantId === p.participantId }"
              @click="selectedParticipant = p"
            >
              <div class="pr-top">
                <span class="pr-id">{{ p.participantDisplayId }}</span>
                <span class="pr-sub">{{ p.participantId }}</span>
              </div>
              <div class="pr-bottom">
                <span class="assign-chip" v-if="getAssignedActions(p.participantId).length">
                  Assigned: {{ getAssignedActions(p.participantId).join(', ') }}
                </span>
                <span class="assign-chip none" v-else>Not assigned (read-only)</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <!-- Main -->
      <main class="main">
        <div class="detail-header">
          <div>
            <div class="detail-title">
              {{ selectedParticipant ? selectedParticipant.participantDisplayId : 'Select a participant' }}
              <span v-if="selectedParticipant" class="detail-subtle">
                - {{ selectedParticipant.participantId }}
              </span>
            </div>
            <div class="detail-sub">
              Workbench is a mock of the backend DynamoDB/visit/log/scoring/comment flow.
            </div>
          </div>

          <div v-if="selectedParticipant" class="header-right">
            <span class="header-pill">
              Current allowed actions (mock)
              <span class="header-pill-actions">
                {{ getAssignedActions(selectedParticipant.participantId).length ? getAssignedActions(selectedParticipant.participantId).join(', ') : 'None' }}
              </span>
            </span>

            <span class="info-icon" tabindex="0" role="button"
              aria-label="Info: assignment enforcement is per participant"
              data-tip="Write enforcement on the backend should check an assignment record exists for (participantId, portalUserId) and that AllowedActions includes the operation.">
              i
            </span>
          </div>
        </div>

        <div class="tabs">
          <button
            v-for="t in tabsForRole"
            :key="t.id"
            class="tab-btn"
            :class="{ active: activeTab === t.id }"
            @click="activeTab = t.id"
          >
            {{ t.label }}
          </button>
        </div>

        <div class="tab-panel">
          <!-- Tasks -->
          <section v-if="activeTab === 'tasks'" class="panel">
            <div class="panel-title">
              Tasks / Status
              <span class="info-icon" tabindex="0" role="button"
                data-tip="Mock tasks represent what the real dashboard would compute from ACTInterview visit types + scoring status + open change requests.">
                i
              </span>
            </div>

            <table class="task-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Status</th>
                  <th>Open requests</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in mockTasks" :key="task.name">
                  <td>{{ task.name }}</td>
                  <td><span class="task-status" :class="task.statusClass">{{ task.statusLabel }}</span></td>
                  <td>
                    <span v-if="task.openRequests > 0" class="open-req-chip">Flag {{ task.openRequests }}</span>
                    <span v-else class="none-chip">-</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Logs -->
          <section v-if="activeTab === 'logs'" class="panel">
            <div class="panel-title">
              Logs (DynamoDB metadata + S3 discovery)
              <span class="info-icon" tabindex="0" role="button"
                data-tip="Real flow: load visit by (participantId, visitTypeId) -> from visit read visitId -> query ACTInterviewLog for that visitId. For remaining logs, service may use S3 prefix and SessionSummary keys.">
                i
              </span>
            </div>

            <div class="grid-2">
              <div class="card">
                <div class="card-title">Log list (mock)</div>
                <div v-for="l in mockLogs" :key="l.id" class="log-row">
                  <div class="log-left">
                    <span class="log-type">{{ l.typeLabel }}</span>
                    <span class="log-label">{{ l.label }}</span>
                  </div>
                  <button class="btn-mini" type="button" disabled title="Mock only">
                    Load
                  </button>
                </div>
              </div>

              <div class="card">
                <div class="card-title">Log contents (mock)</div>
                <pre class="code-block">{{ mockSelectedLogPreview }}</pre>
              </div>
            </div>
          </section>

          <!-- Scoring -->
          <section v-if="activeTab === 'scoring'" class="panel">
            <div class="panel-title">
              Scoring + Comments
              <span class="info-icon" tabindex="0" role="button"
                data-tip="Writes should enforce: (1) user has scoring write action from Assignments table; (2) store scoring under PK=P#<visitId> and SK=SCORING#<scoringType>; (3) create comments under PK=V#<visitId> and SK prefix LOG#<logId>#COM#.">
                i
              </span>
            </div>

            <div class="grid-2">
              <div class="card">
                <div class="card-title">
                  Scoring form (mock)
                </div>

                <label class="label">
                  Scoring type
                  <span class="info-icon small" tabindex="0" role="button"
                    data-tip="Mock selection. Real would map scoringType -> ACTInterviewScoring items under SK=SCORING#<scoringType>.">
                    i
                  </span>
                </label>

                <select v-model="mockScoringType" class="select">
                  <option v-for="t in scoringTypes" :key="t" :value="t">{{ t }}</option>
                </select>

                <label class="label">
                  Score value
                  <span class="info-icon small" tabindex="0" role="button"
                    data-tip="Mock input. In real API, this is sent to CreateOrUpdateScoring.">
                    i
                  </span>
                </label>

                <input v-model="mockScoreValue" type="number" class="input" placeholder="e.g. 18" />

                <button
                  class="btn-primary"
                  :disabled="!canWrite('scoring')"
                  :title="canWrite('scoring') ? '' : 'Backend should forbid write if assignment missing' "
                  @click="mockSaveScoring"
                >
                  Save scoring (mock)
                </button>

                <div class="hint" v-if="!canWrite('scoring')">
                  Read-only: you are not assigned for scoring writes on this participant.
                </div>
              </div>

              <div class="card">
                <div class="card-title">Comments</div>

                <div class="comment-row" v-for="c in mockComments" :key="c.id">
                  <div class="comment-avatar" :style="{ background: c.color }">{{ c.initials }}</div>
                  <div class="comment-body">
                    <div class="comment-meta">
                      <span class="comment-author">{{ c.author }}</span>
                      <span class="comment-time">{{ c.time }}</span>
                    </div>
                    <div class="comment-text">{{ c.text }}</div>
                  </div>
                </div>

                <label class="label">
                  Add comment
                  <span class="info-icon small" tabindex="0" role="button"
                    data-tip="Mock input. Real flow: CreateComment stores under ACTInterviewComment with PK=V#<visitId> and SK prefix LOG#<logId>#COM#.">
                    i
                  </span>
                </label>
                <textarea v-model="mockNewComment" class="textarea" rows="3" placeholder="Write a comment..." />
                <button
                  class="btn-primary"
                  :disabled="!canWrite('comments') || !mockNewComment.trim()"
                  @click="mockPostComment"
                >
                  Post comment (mock)
                </button>
              </div>
            </div>
          </section>

          <!-- History -->
          <section v-if="activeTab === 'history'" class="panel">
            <div class="panel-title">
              History / Change log (mock)
              <span class="info-icon" tabindex="0" role="button"
                data-tip="Mock. In real app, history is derived from scoring/comment/change-request events (often via logs/scoring/comments stored in DynamoDB).">
                i
              </span>
            </div>

            <div class="history-row" v-for="h in mockHistory" :key="h.id">
              <div class="history-avatar" :style="{ background: h.color }">{{ h.initials }}</div>
              <div class="history-body">
                <div class="history-meta">
                  <span class="history-actor">{{ h.actor }}</span>
                  <span class="history-time">{{ h.time }}</span>
                  <span class="history-tag">{{ h.type }}</span>
                </div>
                <div class="history-text">{{ h.text }}</div>
              </div>
            </div>
          </section>

          <!-- Assignments admin -->
          <section v-if="activeTab === 'assignments'" class="panel">
            <div class="panel-title">
              Assignments (Admin mock editor)
              <span class="info-icon" tabindex="0" role="button"
                data-tip="Mock editor UI. In real implementation, this should be controlled by secure backend endpoints (not by direct Dynamo writes from the browser).">
                i
              </span>
            </div>

            <div class="grid-2">
              <div class="card">
                <div class="card-title">Users assigned to this lGroup (mock)</div>
                <div class="muted">In real system, current user is resolved from Cognito -> portalUserId, then used to enforce per-participant write access.</div>

                <div class="user-grid">
                  <div v-for="u in users" :key="u.id" class="user-pill">
                    <span class="user-dot" :style="{ background: u.color }"></span>
                    {{ u.label }}
                  </div>
                </div>
              </div>

              <div class="card">
                <div class="card-title">Edit assignments for selected participant (mock)</div>
                <div v-if="!selectedParticipant" class="empty-state">Select a participant first.</div>
                <div v-else>
                  <div class="assign-editor-row">
                    <div class="assign-editor-label">Participant</div>
                    <div class="assign-editor-value">{{ selectedParticipant.participantDisplayId }} ({{ selectedParticipant.participantId }})</div>
                  </div>

                  <div class="assign-editor-table">
                    <div class="assign-head">
                      <div>User</div>
                      <div>AllowedActions</div>
                    </div>
                    <div v-for="u in users" :key="u.id" class="assign-line">
                      <div class="assign-user">
                        <span class="user-dot" :style="{ background: u.color }"></span>
                        {{ u.label }}
                      </div>
                      <div class="assign-actions">
                        <label class="check">
                          <input type="checkbox"
                            :checked="mockAssignmentState[selectedParticipant.participantId]?.[u.id]?.includes('visit')"
                            @change="toggleAction(selectedParticipant.participantId, u.id, 'visit')"
                          />
                          Write visit
                        </label>
                        <label class="check">
                          <input type="checkbox"
                            :checked="mockAssignmentState[selectedParticipant.participantId]?.[u.id]?.includes('scoring')"
                            @change="toggleAction(selectedParticipant.participantId, u.id, 'scoring')"
                          />
                          Write scoring
                        </label>
                        <label class="check">
                          <input type="checkbox"
                            :checked="mockAssignmentState[selectedParticipant.participantId]?.[u.id]?.includes('comments')"
                            @change="toggleAction(selectedParticipant.participantId, u.id, 'comments')"
                          />
                          Write comments
                        </label>
                      </div>
                    </div>
                  </div>

                  <button class="btn-primary" @click="mockSaveAssignments" type="button">
                    Save assignments (mock)
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActDashboardMockup',
  data() {
    return {
      // Mock user roles (global capabilities)
      roles: [
        { id: 'interviewer', label: 'Interviewer', color: '#317391' },
        { id: 'reviewer', label: 'Reviewer', color: '#7c3aed' },
        { id: 'admin', label: 'Assignment Admin', color: '#d97706' }
      ],
      currentRoleId: 'interviewer',

      lGroups: [
        { id: 'LGROUP-1', label: 'lGroup LGROUP-1' }
      ],
      selectedLGroup: 'LGROUP-1',

      participantSearch: '',
      selectedParticipant: null,

      // Main tabs in the workbench
      activeTab: 'tasks',
      workbenchTabs: [
        { id: 'tasks', label: 'Tasks' },
        { id: 'logs', label: 'Logs' },
        { id: 'scoring', label: 'Scoring + Comments' },
        { id: 'history', label: 'History' },
        { id: 'assignments', label: 'Assignments (Admin)' }
      ],

      // Users (assignment "owners") shown in assignment editor (mock)
      users: [
        { id: 'u-interviewer', label: 'Interviewer A', color: '#317391' },
        { id: 'u-reviewer', label: 'Reviewer B', color: '#7c3aed' }
      ],

      participants: [
        { participantId: 'P-ACT-001', participantDisplayId: 'ACT001' },
        { participantId: 'P-ACT-002', participantDisplayId: 'ACT002' },
        { participantId: 'P-ACT-003', participantDisplayId: 'ACT003' },
        { participantId: 'P-ACT-004', participantDisplayId: 'ACT004' }
      ],

      scoringTypes: ['Verbal Fluency - Supermarket', 'Verbal Fluency - Letter F'],
      mockScoringType: 'Verbal Fluency - Supermarket',
      mockScoreValue: 18,

      mockNewComment: '',
      mockAssignmentState: {
        // participantId -> userId -> actions[]
        'P-ACT-001': {
          'u-interviewer': ['visit', 'scoring', 'comments'],
          'u-reviewer': ['visit', 'scoring', 'comments']
        },
        'P-ACT-002': {
          'u-interviewer': ['visit', 'scoring'],
          'u-reviewer': ['comments']
        },
        'P-ACT-003': {
          'u-interviewer': ['visit'],
          'u-reviewer': ['visit', 'scoring', 'comments']
        },
        'P-ACT-004': {
          'u-interviewer': [],
          'u-reviewer': []
        }
      }
    };
  },
  computed: {
    filteredParticipants() {
      const q = (this.participantSearch || '').trim().toLowerCase();
      if (!q) return this.participants;
      return this.participants.filter(p => p.participantDisplayId.toLowerCase().includes(q));
    },

    canWriteForCurrentRole() {
      // Map role -> assignment userId for mock UI
      if (this.currentRoleId === 'interviewer') return 'u-interviewer';
      if (this.currentRoleId === 'reviewer') return 'u-reviewer';
      return null; // admin uses editor, not enforcement
    },

    mockTasks() {
      // Tasks are mock: show open requests depending on participant
      const pid = this.selectedParticipant ? this.selectedParticipant.participantId : null;
      const open = pid === 'P-ACT-002' ? 2 : pid === 'P-ACT-003' ? 1 : 0;
      return [
        { name: 'VF - Supermarket', statusLabel: open ? 'Review needed' : 'Scored', statusClass: open ? 'pending' : 'scored', openRequests: open },
        { name: 'VF - Letter F', statusLabel: 'Scored', statusClass: 'scored', openRequests: 0 },
        { name: 'Clock Drawing', statusLabel: 'Scored', statusClass: 'scored', openRequests: 0 }
      ];
    },

    mockLogs() {
      return [
        { id: 'l1', typeLabel: 'JSON', label: 'Session Summary Log (Battery summary)' },
        { id: 'l2', typeLabel: 'JSON', label: 'Battery log: VF task parameters' },
        { id: 'l3', typeLabel: 'Audio', label: 'Audio: Speech segment (wav)' }
      ];
    },

    mockSelectedLogPreview() {
      if (!this.selectedParticipant) return 'Select a participant to preview logs...';
      const pid = this.selectedParticipant.participantId;
      return [
        `// Mock preview for visit/scoped logs`,
        `visitId lookup input: participantId=${pid}`,
        `example: ACTInterviewLog PK=V#<visitId> SK prefix="LOG#"` ,
        ``,
        `{`,
        `  "logType": "SessionSummary",`,
        `  "sourceS3Prefix": "studyUpload/..../${pid}/...",`,
        `  "hint": "Real content is loaded from DynamoDB metadata + S3 payload."`,
        `}`
      ].join('\n');
    },

    mockComments() {
      // Mock comments based on participant
      const pid = this.selectedParticipant ? this.selectedParticipant.participantId : null;
      if (pid === 'P-ACT-002') {
        return [
          { id: 'c1', author: 'Reviewer B', initials: 'RB', color: '#7c3aed', time: '2 days ago', text: 'Notes: audio was unclear on apple juice segment.' }
        ];
      }
      if (pid === 'P-ACT-003') {
        return [
          { id: 'c1', author: 'Interviewer A', initials: 'IA', color: '#317391', time: '3 days ago', text: 'Submitted initial scoring. Please verify mark changes.' }
        ];
      }
      return [
        { id: 'c1', author: 'Interviewer A', initials: 'IA', color: '#317391', time: 'Today', text: 'Participant paused - reviewing flagged segments.' }
      ];
    },

    mockHistory() {
      const pid = this.selectedParticipant ? this.selectedParticipant.participantId : null;
      const base = [
        { id: 'h1', actor: 'Interviewer A', initials: 'IA', color: '#317391', time: 'Feb 10 | 9:22 AM', type: 'score-entry', text: 'Scored VF - Supermarket. apple juice marked C.' },
        { id: 'h2', actor: 'Reviewer B', initials: 'RB', color: '#7c3aed', time: 'Feb 12 | 2:14 PM', type: 'request', text: 'Sent back: please fix apple juice mark.' }
      ];
      if (pid === 'P-ACT-001') return base;
      if (pid === 'P-ACT-002') return base.slice(0, 1);
      if (pid === 'P-ACT-003') return base.reverse();
      return [];
    },

    tabsForRole() {
      const all = this.workbenchTabs || [];
      if (this.currentRoleId === 'admin') return all;
      return all.filter(t => t.id !== 'assignments');
    }
  },
  watch: {
    // Ensure selected participant exists for workbench
    selectedParticipant(n) {
      if (n && this.activeTab === 'assignments') {
        // keep
      }
    },
    currentRoleId() {
      // If switching roles, go back to tasks for clarity
      if (this.selectedParticipant) this.activeTab = 'tasks';
    }
  },
  methods: {
    getAssignedActions(participantId) {
      if (!participantId) return [];
      const userId = this.canWriteForCurrentRole;
      if (!userId) return [];
      const entry = this.mockAssignmentState[participantId] || {};
      return entry[userId] || [];
    },

    canWrite(actionKind) {
      // actionKind: visit | scoring | comments
      if (!this.selectedParticipant) return false;
      if (this.currentRoleId === 'admin') return false; // admin tab is editor only
      const allowed = this.getAssignedActions(this.selectedParticipant.participantId);
      return allowed.includes(actionKind);
    },

    mockSaveScoring() {
      // Mock action only
      alert('Mock: Save scoring would POST to ACTInterview scoring endpoint after backend assignment check.');
    },

    mockPostComment() {
      alert('Mock: Post comment would POST to ACTInterview comments endpoint after backend assignment check.');
      this.mockNewComment = '';
    },

    toggleAction(participantId, userId, action) {
      if (!this.mockAssignmentState[participantId]) this.mockAssignmentState[participantId] = {};
      if (!this.mockAssignmentState[participantId][userId]) this.mockAssignmentState[participantId][userId] = [];
      const list = this.mockAssignmentState[participantId][userId];
      const idx = list.indexOf(action);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(action);
      // Ensure reactivity in Vue 2/3 options API
      this.mockAssignmentState[participantId][userId] = [...list];
    },

    mockSaveAssignments() {
      alert('Mock: Save assignments would call secure backend endpoints (server writes to DynamoDB).');
    }
  },
  mounted() {
    // Pick a default participant for faster demo
    this.selectedParticipant = this.participants[0] || null;
  }
};
</script>

<style scoped>
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}
.mockup-badge {
  font-size: 10px;
  font-weight: 800;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
}
.mockup-bar-sep { color: #d1d5db; }
.mockup-bar-label { color: #9ca3af; }
.mockup-back { margin-left: auto; color: #9ca3af; text-decoration: none; font-size: 12px; }
.mockup-back:hover { color: #64748b; }

.layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  padding: 18px;
}

.sidebar {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
  height: fit-content;
  position: sticky;
  top: 80px;
}

.section + .section { margin-top: 16px; }
.section-title {
  font-weight: 700;
  color: #111827;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.role-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.role-btn {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  font-weight: 700;
  color: #374151;
}
.role-btn:hover { border-color: #3986A8; color: #237091; }
.role-btn.active { background: #3986A8; border-color: #3986A8; color: #fff; }

.select, .input, .search, .textarea {
  width: 100%;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  padding: 10px 12px;
  font-size: 14px;
}
.select:focus, .input:focus, .search:focus, .textarea:focus {
  outline: none;
  border-color: #237091;
}
.textarea { resize: vertical; }
.search { margin-bottom: 12px; }

.participant-list {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #f9fafb;
}
.participant-row {
  padding: 12px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eef2f7;
}
.participant-row:last-child { border-bottom: none; }
.participant-row:hover { background: #eef6ff; }
.participant-row.active { background: #e8f4f8; }
.pr-top { display: flex; gap: 10px; align-items: baseline; justify-content: space-between; }
.pr-id { font-weight: 800; color: #237091; }
.pr-sub { font-size: 12px; color: #9ca3af; }
.pr-bottom { margin-top: 6px; }
.assign-chip {
  display: inline-block;
  padding: 6px 8px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}
.assign-chip.none { background: #f3f4f6; color: #6b7280; font-weight: 600; }

.main {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}
.detail-header {
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.detail-title {
  font-size: 18px;
  font-weight: 900;
  color: #111827;
}
.detail-subtle { font-size: 12px; font-weight: 700; color: #6b7280; }
.detail-sub { font-size: 13px; color: #6b7280; margin-top: 4px; }
.header-right { display: flex; align-items: center; gap: 12px; }
.header-pill {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
}
.header-pill-actions { color: #237091; font-weight: 900; margin-left: 6px; }

.info-icon {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #e0f2fe;
  border: 1px solid #93c5fd;
  color: #0e7490;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 900;
  position: relative;
  flex-shrink: 0;
  cursor: help;
  user-select: none;
}
.info-icon::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  top: 26px;
  min-width: 0;
  max-width: 90vw;
  width: 320px;
  transform: translateX(-50%) translateY(-4px);
  white-space: normal;
  padding: 10px 12px;
  border-radius: 10px;
  background: #111827;
  color: #f9fafb;
  font-size: 12px;
  line-height: 1.35;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease, transform 0.12s ease;
  z-index: 50;
}
.info-icon:hover::after,
.info-icon:focus::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}
.info-icon.small {
  width: 16px;
  height: 16px;
  font-size: 10px;
}
.tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}
.tab-btn {
  padding: 10px 14px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 900;
  color: #6b7280;
}
.tab-btn:hover { color: #237091; }
.tab-btn.active { color: #237091; border-bottom: 3px solid #3986A8; }

.tab-panel { padding: 14px 18px; }
.panel { padding: 6px 0 14px; }
.panel-title {
  font-weight: 900;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
}
.card-title { font-weight: 900; margin-bottom: 10px; color: #111827; }

.task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.task-table th {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 2px solid #e5e7eb;
  font-size: 12px;
  letter-spacing: 0.04em;
  color: #6b7280;
  text-transform: uppercase;
}
.task-table td {
  padding: 10px 10px;
  border-bottom: 1px solid #eef2f7;
}
.task-status {
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  display: inline-block;
}
.task-status.scored { background: #dcfce7; color: #166534; }
.task-status.pending { background: #f1f5f9; color: #475569; }
.open-req-chip {
  background: #fef3c7;
  color: #92400e;
  font-weight: 900;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
}
.none-chip { color: #9ca3af; font-weight: 800; }

.log-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #eef2f7;
}
.log-row:last-child { border-bottom: none; }
.log-left { display: flex; flex-direction: column; gap: 2px; }
.log-type { font-weight: 900; color: #237091; font-size: 12px; }
.log-label { color: #374151; font-weight: 700; font-size: 13px; }
.btn-mini {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 900;
}
.btn-mini:disabled { opacity: 0.7; cursor: not-allowed; }

.code-block {
  margin: 0;
  padding: 12px;
  border-radius: 10px;
  background: #0b1220;
  color: #e5e7eb;
  font-size: 12px;
  line-height: 1.35;
  overflow: auto;
}
.label {
  font-weight: 900;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0 8px;
  color: #111827;
}
.input { margin-bottom: 12px; }
.textarea { margin-top: 8px; }
.btn-primary {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: #3986A8;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
  margin-top: 12px;
}
.btn-primary:disabled { background: #cbd5e1; cursor: not-allowed; }
.hint { margin-top: 10px; color: #6b7280; font-weight: 700; font-size: 13px; }

.comment-row { display: flex; gap: 10px; padding: 10px 0; border-bottom: 1px solid #eef2f7; }
.comment-row:last-child { border-bottom: none; }
.comment-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 900;
  flex-shrink: 0;
}
.comment-body { flex: 1; min-width: 0; }
.comment-meta { display: flex; gap: 10px; align-items: center; margin-bottom: 4px; }
.comment-author { font-weight: 900; color: #111827; }
.comment-time { margin-left: auto; color: #9ca3af; font-weight: 800; font-size: 12px; }
.comment-text { color: #374151; font-weight: 700; font-size: 13px; line-height: 1.35; }

.history-row { display: flex; gap: 10px; padding: 12px 0; border-bottom: 1px solid #eef2f7; }
.history-row:last-child { border-bottom: none; }
.history-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 900;
  flex-shrink: 0;
}
.history-body { flex: 1; min-width: 0; }
.history-meta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.history-actor { font-weight: 900; color: #111827; }
.history-time { color: #9ca3af; font-weight: 800; margin-left: auto; font-size: 12px; }
.history-tag {
  background: #f1f5f9;
  color: #475569;
  font-weight: 900;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
}
.history-text { margin-top: 6px; color: #374151; font-weight: 700; }

.user-grid { display: flex; flex-wrap: wrap; gap: 10px; }
.user-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-weight: 900;
  color: #374151;
  font-size: 13px;
}
.user-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

.muted { color: #6b7280; font-weight: 700; font-size: 13px; line-height: 1.35; }
.empty-state { color: #9ca3af; font-weight: 800; padding: 12px 0; }

.assign-editor-row {
  display: flex;
  gap: 10px;
  align-items: baseline;
  margin-bottom: 10px;
}
.assign-editor-label { font-weight: 900; color: #111827; }
.assign-editor-value { color: #374151; font-weight: 800; }
.assign-editor-table { border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
.assign-head {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 10px 12px;
  gap: 10px;
  font-weight: 900;
  color: #6b7280;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #f9fafb;
  border-bottom: 1px solid #eef2f7;
}
.assign-line {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 10px 12px;
  gap: 10px;
  border-bottom: 1px solid #eef2f7;
  align-items: center;
}
.assign-line:last-child { border-bottom: none; }
.assign-user { font-weight: 900; color: #374151; display: flex; align-items: center; gap: 10px; }
.assign-actions { display: flex; flex-wrap: wrap; gap: 10px; }
.check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: #374151;
  font-size: 13px;
}

@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
  .sidebar { position: relative; top: 0; }
  .grid-2 { grid-template-columns: 1fr; }
}
</style>

